import { isEmpty } from 'lodash'
import { useState } from 'react'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Button } from '~/components/ui/atoms/button/Button'
import { Modal } from '~/components/ui/molecules/modal/Modal'

interface SaveNewTableTabDialogProps {
  defaultName: string
  onClose: () => void
  onConfirm: (name: string) => Promise<void>
  tableName: string
}

/**
 * Renders the dialog to name a new table view before creating it, prefilled with a default
 * name (table name + timestamp) that the user can edit before confirming.
 */
export const SaveNewTableTabDialog = ({
  defaultName,
  onClose,
  onConfirm,
  tableName,
}: SaveNewTableTabDialogProps) => {
  const { t } = useTypedTranslation()
  const [name, setName] = useState(defaultName)
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setIsSaving(true)
    await onConfirm(name).finally(() => setIsSaving(false))
  }

  return (
    <Modal
      actions={
        <Button
          disabled={isEmpty(name.trim()) || isSaving}
          onClick={handleSave}
          variant="secondary"
        >
          {t('common.actions.save')}
        </Button>
      }
      disableRestoreFocus
      onClose={onClose}
      open
      title={t('table_tabs.save_as_new_view_modal_title', { tableName })}
    >
      <ShortTextInput
        autoFocus
        onChange={(e) => setName(e.target.value)}
        onClick={(event) => event.stopPropagation()}
        value={name}
      />
    </Modal>
  )
}
